import { keepPreviousData, queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap, type ApiClient } from '../client';
import type { components, operations } from '../schema.gen';
import { queryKeys } from './keys';

export type SearchLineItem = components['schemas']['SearchLineItem'];
export type LineItemSearchPage = components['schemas']['LineItemSearchPage'];
type ApiLineItemQuery = NonNullable<operations['pay_line_items_list']['parameters']['query']>;
/** The query, where any filter may be undefined (left out of the request). */
export type LineItemQuery = { [K in keyof ApiLineItemQuery]?: ApiLineItemQuery[K] | undefined };

/** A page of line items matching the filters (undefined ones are left out). */
export function lineItemsQuery(api: ApiClient, query: LineItemQuery) {
  const params = Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== null),
  ) as ApiLineItemQuery;
  return queryOptions({
    queryKey: queryKeys.lineItems(params as Record<string, string | number | undefined>),
    queryFn: () => unwrap(api.GET('/api/pay/line-items', { params: { query: params } })),
  });
}

/** Keeps the last page on screen while the next loads. */
export function useLineItems(query: LineItemQuery) {
  return useQuery({ ...lineItemsQuery(useApiClient(), query), placeholderData: keepPreviousData });
}

/** Customer names in scope containing `q`, for the typeahead. */
export function useCustomers(repId: string | undefined, q: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.customers(repId, q),
    queryFn: async () =>
      (
        await unwrap(
          api.GET('/api/pay/customers', {
            params: { query: { ...(repId ? { repId } : {}), ...(q ? { q } : {}) } },
          }),
        )
      ).items,
    placeholderData: keepPreviousData,
  });
}

export function useProductTypes(repId: string | undefined) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.productTypes(repId),
    queryFn: async () =>
      (
        await unwrap(
          api.GET('/api/pay/product-types', { params: { query: repId ? { repId } : {} } }),
        )
      ).items,
  });
}

export type LineItemExport = components['schemas']['LineItemExport'];
export type LineItemExportCreate = components['schemas']['LineItemExportCreate'];

/** How often a background export is polled. */
export const EXPORT_POLL_MS = 1500;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function fileNameOf(response: Response, fallback: string) {
  const name = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '');
  return name?.[1] ?? fallback;
}

/**
 * Exports the search's matches as a PDF (pc-o19.5). A small export is the response
 * itself. A large one comes back 202 as an export rendering in the background: it is
 * polled until it's completed (then its file is fetched) or failed (an error). Resolves
 * with the file to save; `onQueued` hears about a background export as it starts.
 */
export function useExportLineItems({
  onQueued,
}: { onQueued?: (queued: LineItemExport) => void } = {}) {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (body: LineItemExportCreate): Promise<{ blob: Blob; fileName: string }> => {
      const result = await api.POST('/api/pay/line-items/exports', { body, parseAs: 'blob' });
      const blob = await unwrap(Promise.resolve(result));
      if (result.response.status !== 202) {
        return { blob, fileName: fileNameOf(result.response, 'line-items.pdf') };
      }
      let current = JSON.parse(await blob.text()) as LineItemExport;
      onQueued?.(current);
      const path = { params: { path: { export_id: current.id } } };
      while (current.status === 'queued') {
        await sleep(EXPORT_POLL_MS);
        current = await unwrap(api.GET('/api/pay/line-items/exports/{export_id}', path));
      }
      if (current.status === 'failed') throw new Error(current.error ?? 'The export failed.');
      const file = await api.GET('/api/pay/line-items/exports/{export_id}/file', {
        ...path,
        parseAs: 'blob',
      });
      return {
        blob: await unwrap(Promise.resolve(file)),
        fileName: fileNameOf(file.response, current.fileName),
      };
    },
  });
}
